import { Box, IconButton, Chip } from "@mui/material";
import { green, orange, red, grey, blue } from "@mui/material/colors";
import { DataGridPro, GRID_DETAIL_PANEL_TOGGLE_FIELD, useGridApiRef } from "@mui/x-data-grid-pro";
import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	approveScenario,
	GetScenarioModificationDetails,
	getScenarioDetails,
	getScenarioHistory,
	getScenariosForApproval,
	setState,
} from "./scenarioApprovalSlice";
import './ScenarioApproval.css'
import ScenarioHistoryDetailPanel from "./ScenarioHistoryDetailPanel";

import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditNoteIcon from "@mui/icons-material/EditNote";
import EditIcon from '@mui/icons-material/Edit';
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { setState as setScenarioState } from "../../Setup/Scenario/scenarioSetupNewSlice";
import { setState as setEditScenarioState, getDataSources, getScenario, getCustGrpRef, getScenarioDetails as ScenarioDetails, getScDSAData, getUserCountriesEdit } from "../../Setup/Scenario/Edit/editScenarioNewSlice";
import dayjs from "dayjs";
import moment from "moment";
import muiConfig from '../../../assets/MuiConfig.json'

function getCriticalityChipProps(params) {
	const raw = params.value;
	const normalized = (raw ?? "").toString().trim().toLowerCase();
	const label = (raw ?? "").toString().trim() || "—";

	if (!normalized) {
		return {
			label: "—",
			style: {
				fontWeight: "bold",
				backgroundColor: grey[100],
				color: grey[600],
			},
		};
	}

	if (normalized === "low") {
		return {
			label,
			style: {
				fontWeight: "bold",
				backgroundColor: green[50],
				color: green[500],
			},
		};
	}
	if (normalized === "medium") {
		return {
			label,
			style: {
				fontWeight: "bold",
				borderColor: orange[500],
				backgroundColor: orange[50],
				color: orange[500],
			},
		};
	}
	if (normalized === "high") {
		return {
			label,
			style: {
				fontWeight: "bold",
				borderColor: red[500],
				backgroundColor: red[50],
				color: red[500],
			},
		};
	}
	if (normalized === "severe") {
		return {
			label,
			style: {
				fontWeight: "bold",
				borderColor: grey[700],
				backgroundColor: grey[100],
				color: grey[700],
			},
		};
	}
	return {
		label,
		style: {
			fontWeight: "bold",
			borderColor: blue[500],
			backgroundColor: blue[50],
			color: blue[500],
		},
	};
}

function getScenariosForApprovalColumns(countries) {
	const w = 200;
	const columns = [
		{ field: "scenarioName", headerName: "Scenario", flex: 1 },
		{
			field: "criticality",
			headerName: "Criticality",
			flex: 1,
			align: "center",
			headerAlign: "center",
			renderCell: (params) => (
				<Chip
					sx={{ height: "1.8rem", minHeight: "1.8rem", padding: "0.5rem" }}
					{...getCriticalityChipProps(params)}
				/>
			),
		},
		{
			field: "geoId",
			headerName: "Country Code",
			flex: 1,
			valueGetter: (params) => {
				const countryCode = countries.find(c => c.countryID === params.row.countryID).countryCode;
				return countryCode;
			}
		},
		{ field: "scenarioStatus", headerName: "Scenario Status", flex: 1 },
		{
			field: "updatedDate", headerName: "Last Action Date", flex: 1, valueFormatter: (params) =>moment(params.value).format('DD/MM/YYYY')
		},
		{ field: "updatedBy", headerName: "Last Action By", flex: 1 },
		{
			field: "reject",
			headerName: "Reject Scenario",
			flex: 1,
			renderCell: RejectButton,
			align:"center",
			headerAlign:"center",
		},
		{
			field: "approve",
			headerName: "Approve Scenario",
			flex: 1,
			renderCell: ApproveButton,
			align:"center",
			headerAlign:"center",
		},
		{
			field: "isEdit",
			headerName: "Edit Scenario",
			flex: 1,
			renderCell: EditButton,
			align:"center",
			headerAlign:"center",
		},
	];
	return columns;


}


export default function Table() {
	const dispatch = useDispatch();
	const state = useSelector((store) => store.scenarioApproval);
	const { scenariosForApprovalTable, countries, selectedRows } = state;
	const { rows } = scenariosForApprovalTable;
	const [isAll, setIsAll] = useState(false); 
	const columns = getScenariosForApprovalColumns(countries);
	const [paginationModel, setPaginationModel] = useState({
		pageSize: 20,
		page: 0
	});
	const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState([]);
	const [detailPanelLayout, setDetailPanelLayout] = useState({ left: 0, width: 0 });
	const apiRef = useGridApiRef();

	const columnVisibilityModel = useMemo(
		() => ({ [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false }),
		[]
	);

	const updateDetailPanelLayout = useCallback(() => {
		const api = apiRef.current;
		if (!api?.getColumnHeaderElement) return;

		const rootElement = api.rootElementRef?.current;
		const scenarioHeader = api.getColumnHeaderElement("scenarioName");
		const approveHeader = api.getColumnHeaderElement("approve");
		if (!rootElement || !scenarioHeader || !approveHeader) return;

		const rootRect = rootElement.getBoundingClientRect();
		const startRect = scenarioHeader.getBoundingClientRect();
		const endRect = approveHeader.getBoundingClientRect();

		setDetailPanelLayout({
			left: Math.max(0, Math.round(startRect.left - rootRect.left)),
			width: Math.max(0, Math.round(endRect.right - startRect.left)),
		});
	}, [apiRef]);

	useEffect(() => {
		setDetailPanelExpandedRowIds([]);
	}, [scenariosForApprovalTable]);

	useEffect(() => {
		if (detailPanelExpandedRowIds.length === 0) return undefined;

		const frameId = requestAnimationFrame(updateDetailPanelLayout);
		window.addEventListener("resize", updateDetailPanelLayout);
		return () => {
			cancelAnimationFrame(frameId);
			window.removeEventListener("resize", updateDetailPanelLayout);
		};
	}, [
		detailPanelExpandedRowIds,
		updateDetailPanelLayout,
		paginationModel,
		isAll,
	]);

	const getDetailPanelContent = useCallback(
		({ row }) => (
			<ScenarioHistoryDetailPanel row={row} layout={detailPanelLayout} />
		),
		[detailPanelLayout]
	);

	const getDetailPanelHeight = useCallback(() => "auto", []);
	//    useEffect(()=>{
	// 	  let TableData = JSON.parse(JSON.stringify(scenariosForApprovalTable));
	// 	  for( let i = 0; i< TableData.rows.length; i++){
	// 		TableData.rows[i] = getApproveStatus(TableData.rows[i]);
	// 	  }
	// 	  dispatch(setState("scenariosForApprovalTable", TableData ));

	//    },[scenariosForApprovalTable])



	const fetchModificationDetails = (scenarioId, scenarioModificationID = 0) => {
		dispatch(setState("selectedScenarioModificationID", scenarioModificationID));
		dispatch(
			GetScenarioModificationDetails({
				ScenarioID: scenarioId,
				ScenarioModificationID: scenarioModificationID,
			})
		);
	};

	const handleRowClick = (rowData) => {
		if (rowData != undefined) {
			const scenarioId = rowData.row.id;
			dispatch(getScenarioDetails({ scenarioId }));
			fetchModificationDetails(scenarioId, 0);

			const isExpanded = detailPanelExpandedRowIds.includes(scenarioId);
			if (isExpanded) {
				setDetailPanelExpandedRowIds([]);
				dispatch(setState("selectedHistoryScenarioID", null));
			} else {
				setDetailPanelExpandedRowIds([scenarioId]);
				dispatch(getScenarioHistory({ ScenarioID: scenarioId }));
				requestAnimationFrame(updateDetailPanelLayout);
			}
		}
		if (rowData.length === 0) {
			dispatch(setState("scenarioDetails", null));
		}

	}
	const handleCellClick = (param, event) =>{
		if(param.field === "reject" || param.field === "approve" || param.field === "isEdit"){
			event.stopPropagation();
		}
	}
    const setModal = (newPaginationModel) => {
		if(newPaginationModel.pageSize === muiConfig.DataGrid_All && newPaginationModel.page === 0 && rows?.length > 100){
		setIsAll(true);
		}else{
		setIsAll(false);
		}
		setPaginationModel(newPaginationModel);  
	}
	return (
		<Box sx={{ mt: 3 }}>
			<DataGridPro
				className="scenario-approval-grid"
				apiRef={apiRef}
				rows={rows}
				columns={columns}
				columnVisibilityModel={columnVisibilityModel}
				onRowSelectionModelChange={(selRows) => {
					dispatch(setState("selectedRows", selRows));
					//    if(selRows.length > 0) {
					//       const scenarioId = selRows[selRows.length - 1];
					//       dispatch(getScenarioDetails({ scenarioId }));
					//    }
					//    if(selRows.length === 0) {
					//       dispatch(setState("scenarioDetails", null));
					//    }
				}}				
				sx={{
					'& .MuiDataGrid-cell': {
						whiteSpace: 'normal !important',
						wordWrap: 'break-word !important',
					},
					...(!isAll ? {
						"& .MuiDataGrid-virtualScroller": {
						   maxHeight: "unset !important"
						}
					  } : {
						"& .MuiDataGrid-virtualScroller": {
						   maxHeight: "80vh !important"
						}
					})
				}}
				rowSelectionModel={selectedRows}
				checkboxSelection
				disableColumnMenu
				//isRowSelectable={false}
				//isRowSelectable={(params) =>  params.row.scenarioStatus !== "Approved" && params.row.scenarioStatus !== "Rejected"}
				isRowSelectable={(params) => params.row.canApproveReject === true}
				disableRowSelectionOnClick
				initialState={{
					pagination: { paginationModel: { pageSize:20, page:0 }}
				}}
				pagination
				pageSizeOptions={[20, 50, 100, { value: muiConfig.DataGrid_All , label: 'All' }]}
				paginationModel={paginationModel}
				onPaginationModelChange = {setModal}
				onColumnWidthChange={updateDetailPanelLayout}
				// getRowClassName = {(params) => 
				// 	params.row.scenarioStatus === "Approved" || params.row.scenarioStatus === "Rejected"? 'disabled-row': ''
				// }
				getRowClassName={(params) =>
					params.row.canApproveReject !== true ? 'disabled-row' : ''
				}
				onRowClick={handleRowClick}
				onCellClick = {handleCellClick}
				headerHeight={40}
				getDetailPanelContent={getDetailPanelContent}
				getDetailPanelHeight={getDetailPanelHeight}
				detailPanelExpandedRowIds={detailPanelExpandedRowIds}
				onDetailPanelExpandedRowIdsChange={(ids) => {
					setDetailPanelExpandedRowIds(ids);
					if (ids.length === 0) {
						dispatch(setState("selectedHistoryScenarioID", null));
					}
				}}
			/>
		</Box>
	);
}

// #region Reject Button
function RejectButton(params) {
	const autherization = localStorage.getItem("data");
	const data = JSON.parse(autherization);
	let setAccess = true;
	if (data?.restrictedAccess == 0) setAccess = true;
	else setAccess = false;
	const isRestrictedAccess = setAccess;
	const dispatch = useDispatch();

	function openRejectModal() {
		dispatch(getScenarioDetails({ scenarioId: params.row.id })).then(() => {
			dispatch(setState("rejectScenarioModalOpen", true));
		});
	}

	if (params.row.canApproveReject && isRestrictedAccess) {
		return (
			<IconButton onClick={openRejectModal}>
				<CancelIcon sx={{ color: "red.main" }} />
			</IconButton>
		);
	}
	return null;
}
// #endregion

// #region Approve Button
function ApproveButton(params) {
	const autherization = localStorage.getItem("data");
	const data = JSON.parse(autherization);
	let setAccess = true;
	if (data?.restrictedAccess == 0) setAccess = true;
	else setAccess = false;
	const isRestrictedAccess = setAccess;
	const dispatch = useDispatch();
	const state = useSelector((store) => store.scenarioApproval);
	const { scenariosForApproval, selectedPeriod, selectedScenarioStatus, selectedCountryID, selectedCountry, searchText } =
		state;

	function search() {
		dispatch(setState("scenarioDetails", null));
		dispatch(setState("rejectSelectedComment", ''));
		dispatch(setState("selectedRows", []));
		const logedUser = JSON.parse(localStorage.getItem("data"));
		const period = selectedPeriod;
		const scenarioStatus = selectedScenarioStatus;
		const countryId = selectedCountryID;
		const obj = {
			logedUser,
			period,
			scenarioStatus,
			searchText,
			countryId
		};
		dispatch(getScenariosForApproval(obj));
	}
	const approve= (e) =>{
		e.stopPropagation();
		const id = params.row.id;
		const details = preparePayload(id);
		const scenario = scenariosForApproval.find((s) => s.scenarioID === id);
		const user = JSON.parse(localStorage.getItem("data"));
		//const scenarioObj = { ...scenario, user: user };
		const scenarioObj = details;
		dispatch(approveScenario(scenarioObj)).then(() => {
			search();
		});
	}

	function preparePayload(id) {
		const user = JSON.parse(localStorage.getItem("data"));
		const scenario = scenariosForApproval.find((s) => s.scenarioID === id);
		console.log(scenario);
		console.log(user);
		const country = user.userCountries.find(
			(index) => index.country.countryID === scenario.countryID
		).country;

		const formData = new FormData();
		formData.append("countryID", scenario.countryID);
		formData.append("countryName", country.countryName);
		formData.append("countryCode", country.countryCode);
		formData.append(
			"countrySalesOrganizationCode",
			country.countrySalesOrganizationCode
		);
		formData.append("scenarioID", scenario.scenarioID);
		formData.append("effectiveStartDate", scenario.effectiveStartDate);
		formData.append("effectiveEndDate", scenario.effectiveEndDate);
		formData.append("scenarioName", scenario.scenarioName);
		formData.append("scenarioStatus", scenario.scenarioStatus);
		formData.append("updatedBy", scenario.updatedBy);
		formData.append("updatedDate", scenario.updatedDate);
		formData.append("createdBy", scenario.createdBy);
		formData.append("createdDate", scenario.createdDate);
		// formData.append("rejectionComments", "");
		formData.append("canApproveReject", true);
		//formData.append("tranOptions[timeout]", scenario.tranOptions.timeout);
		// formData.append(
		// 	"tranOptions[isolationLevel]",
		// 	scenario.tranOptions.isolationLevel
		// );
		formData.append("user[userID]", user.userID);
		formData.append("user[firstName]", user.firstName);
		formData.append("user[lastName]", user.lastName);
		formData.append("user[email]", user.email);
		formData.append("user[roleID]", user.roleID);
		formData.append("user[roleName]", user.roleName);
		formData.append("user[isAdmin]", user.isAdmin);
		formData.append("user[isAnalyst]", user.isAnalyst);
		formData.append("user[isApprover]", user.isApprover);
		formData.append("user[isFirstTimeUser]", user.isFirstTimeUser);
		formData.append("user[isHQUser]", user.isHQUser);
		formData.append("user[isInsert]", user.isInsert);
		formData.append("user[isLocalUser]", user.isLocalUser);
		formData.append("user[activeFlag]", user.activeFlag);
		formData.append("user[isFirstTimeUser]", user.isFirstTimeUser);
		formData.append("user[passwordChangeDate]", user.passwordChangeDate);
		formData.append("user[isIMSUser]", user.isIMSUser);

		user.userCountries.forEach((country, index) => {
			formData.append(
				`user[userCountries][${index}][userID]`,
				country.userID
			);
			formData.append(
				`user[userCountries][${index}][country][countryID]`,
				country.country.countryID
			);
			formData.append(
				`user[userCountries][${index}][country][countryName]`,
				country.country.countryName
			);
			formData.append(
				`user[userCountries][${index}][country][countryCode]`,
				country.country.countryCode
			);
			formData.append(
				`user[userCountries][${index}][country][countrySalesOrganizationCode]`,
				country.country.countrySalesOrganizationCode
			);
		});

		user.userCustomerTypes.forEach((type, index) => {
			formData.append(
				`user[userCustomerTypes][${index}][userID]`,
				type.userID
			);
			formData.append(
				`user[userCustomerTypes][${index}][customerTypeBO][customerTypeCode]`,
				type.customerTypeBO.customerTypeCode
			);
			formData.append(
				`user[userCustomerTypes][${index}][customerTypeBO][customerTypeName]`,
				type.customerTypeBO.customerTypeName
			);
		});
		formData.append("user[token]", user.token);

		return formData;
	}


	if (params.row.canApproveReject && isRestrictedAccess) {
		return (
			<IconButton onClick={approve}>
				<CheckCircleIcon sx={{ color: "green.main" }} />
			</IconButton>
		);
	}
	return null;
}
//#endregion

// #region Edit Button
function EditButton(params) {
	const autherization = localStorage.getItem("data");
	const data = JSON.parse(autherization);
	let setAccess = true;
	if (data?.restrictedAccess == 0) setAccess = true;
	else setAccess = false;
	const isRestrictedAccess = setAccess;
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const scenarioApprovalState = useSelector((store) => store.scenarioApproval);
	const { scenariosForApproval, countries, selectedCountry } = scenarioApprovalState;

	function gotoEditScenario() {
		const id = params.row.id;
		const scenario = scenariosForApproval.find((s) => s.scenarioID === id);
		const { scenarioName, effectiveStartDate, effectiveEndDate } = scenario;

		// #endregion
		dispatch(setScenarioState("editMode", true));
		navigate("/setup/scenario");

		const obj = {
			countryID: scenario.countryID,
			selectedCountry: countries.find((country) => country.countryID === scenario.countryID
			).countryName,
			scenarioName: scenarioName,
			createFromDate: effectiveStartDate,
			createToDate: effectiveEndDate,
			scenarioID: id
		}
		// #region Set Scenario Values
		dispatch(getUserCountriesEdit()).then(()=>{
			dispatch(setEditScenarioState("countryID", scenario.countryID));
			dispatch(setEditScenarioState("selectedCountry", countries.find(
				(country) => country.countryID === scenario.countryID
			).countryName));
		});
		const countryID = scenario.countryID;
		dispatch(getDataSources({ countryID }));
		dispatch(getCustGrpRef({ countryID }));
		const objsearch = {
			countryId: countryID,
			scenario: scenarioName,
			fromDate: "",
			toDate: ""
		};
		console.log("SearchScenario", objsearch);
		dispatch(setEditScenarioState("navigated_ScenarioName", scenarioName));
		dispatch(getScenario(objsearch)).then(() => {
			dispatch(setEditScenarioState("editScID", id));
			dispatch(setEditScenarioState("editScName", scenarioName));
		});
		const objdetails = {
			scenarioId: id,
		};
		dispatch(ScenarioDetails(objdetails));
		dispatch(getScDSAData(objdetails));
	}

	if (params.row.canEdit && isRestrictedAccess) {
		return (
			<IconButton sx={{
				padding:0,
				'&:hover':{
						 backgroundColor:'#F8E9E7',
						 borderRadius:'2px',
					  }
				 }}>
				<EditIcon onClick={gotoEditScenario}  sx={{ 
					color: "#000000",
					fontSize:'large', 
					justifyContent: 'center',
					borderRadius:'2px',
					width:25,
					height:22,
					backgroundColor: 'transparent',
					}} />
			</IconButton>
		);
	}
	return null;
}
// #endregion
